import React, { useState, useMemo, useCallback } from "react";
import styles from "./ProductPricingTable.module.scss";
import SpinningLoader from "@src/components/common/SpinningLoader";
import * as CartApi from "@src/api/CartApi";
import * as SavedDistributorProductApi from "@src/api/SavedDistributorProductApi";
import { useToast } from "@swordsweeper/ssi-ui-component-library";
import {
  DistributorApiHealthStatus,
  DistributorApiHealthStatuses,
} from "@src/constants/DistributorApiHealthStatuses";
import { DistributorProduct } from "@src/api/ProductDetailApi";
import DistributorLogo from "@src/components/common/DistributorLogo";
import filter from "lodash/filter";
import RequiresRoleContainer from "@src/components/containers/RequiresRoleContainer";
import Roles from "@src/constants/Roles";
import { Button } from "@swordsweeper/ssi-ui-component-library";
import { SpinningLoaderSizes } from "@src/components/common/SpinningLoader/SpinningLoaderSizes";
import PriceDisplay from "@src/components/common/PriceDisplay/PriceDisplay";
import { useCurrentUserStore } from "@src/state/currentUserState";
import { useCartStore } from "@src/state/cartState";
import { useSavedForLaterStore } from "@src/state/savedForLaterState";
import Modal, { ModalSizes } from "@src/components/common/Modal";
import { DistributorConnectionStatuses } from "../AccountDistributorSettings/DistributorConnectionConstants";
import { distributorSlugToName } from "@src/constants/Distributors";
import { SimpleTable } from "@swordsweeper/ssi-ui-component-library";

type PricingRow = {
  id: string;
  distributorSlug: string;
  isPreferred?: boolean;
  availabilityStatus: string;
  shippingText?: string;
  rewardsLabel?: string;
  pricePerUnit?: number;
  price: number | undefined;
  quantity?: number;
  hasMorePrices: boolean;
  priceBreaksCount: number;
  minQuantity: number;
  webEligible: boolean;
  buttonText: string;
  buttonDisabled: boolean;
  originalData: DistributorProduct;
  isConnected?: boolean;
  distributorApiHealthStatus: DistributorApiHealthStatus | undefined;
};

interface ProductOtherOptionsProps {
  simplified?: boolean;
  tableHeading?: string;
  data: PricingRow[];
  "data-testid"?: string;
}

const VENDOR_COLUMN_KEY = "vendor";
const SHIPPING_COLUMN_KEY = "shipping";
const AVAILABILITY_COLUMN_KEY = "availability";
const REWARDS_COLUMN_KEY = "rewards";
const PRICE_PER_UNIT_COLUMN_KEY = "pricePerUnit";
const PRICE_COLUMN_KEY = "price";
const ACTION_COLUMN_KEY = "action";

const ALL_TABLE_COLUMN_KEYS = [
  VENDOR_COLUMN_KEY,
  SHIPPING_COLUMN_KEY,
  AVAILABILITY_COLUMN_KEY,
  REWARDS_COLUMN_KEY,
  PRICE_PER_UNIT_COLUMN_KEY,
  PRICE_COLUMN_KEY,
  ACTION_COLUMN_KEY,
];

const SIMPLE_TABLE_ROW_KEY_FIELDS = [
  VENDOR_COLUMN_KEY,
  AVAILABILITY_COLUMN_KEY,
  PRICE_COLUMN_KEY,
  ACTION_COLUMN_KEY,
];

export default function ProductPricingTable({
  simplified,
  tableHeading,
  data,
  "data-testid": dataTestId = "pricing-table",
}: ProductOtherOptionsProps) {
  const { showToast } = useToast();

  const [isAddingItemToCart, setIsAddingItemToCart] = useState(false);
  const [expandedPriceRows, setExpandedPriceRows] = useState<Set<string>>(
    new Set(),
  );
  const [showOrderLimitModal, setShowOrderLimitModal] = useState(false);
  const [orderLimitMessage, setOrderLimitMessage] = useState("");

  const {
    currentUser,
    loading: isUserLoading,
    distributorApiHealthStatuses,
    getDistributorConnection,
    distributorConnections,
  } = useCurrentUserStore();

  const { cartCount, setCart, refreshCartCount } = useCartStore();

  const {
    savedForLaters,
    fetchSavedForLater,
    refreshSavedForLater,
    setSavedForLater,
  } = useSavedForLaterStore();

  const togglePriceExpansion = useCallback((distributorProductId: string) => {
    setExpandedPriceRows((prev) => {
      const next = new Set(prev);
      if (next.has(distributorProductId)) {
        next.delete(distributorProductId);
      } else {
        next.add(distributorProductId);
      }
      return next;
    });
  }, []);

  const removeSavedForLaterIfPresent = useCallback(
    async (distributorProductId: string) => {
      if (!savedForLaters?.length) return;
      const savedItem = savedForLaters.find(
        (item) =>
          `${item.distributorProduct?.id}` === `${distributorProductId}`,
      );
      if (!savedItem) return;

      setSavedForLater(
        savedForLaters.filter((item) => `${item.id}` !== `${savedItem.id}`),
      );
      try {
        await SavedDistributorProductApi.deleteSavedDistributorProduct(
          savedItem.id,
        );
        await refreshSavedForLater();
      } catch (e) {
        console.error(e);
        await refreshSavedForLater();
      }
    },
    [refreshSavedForLater, savedForLaters, setSavedForLater],
  );

  const handleAddToCart = useCallback(
    async (distributorProductId: string) => {
      if (isAddingItemToCart) return;

      const quantityToAdd = 1;

      setIsAddingItemToCart(true);
      try {
        const response = await CartApi.addToCart(
          distributorProductId,
          quantityToAdd,
        );
        if (setCart && response.body.cart) setCart(response.body.cart);
        if (refreshCartCount) {
          await refreshCartCount();
        }
        await removeSavedForLaterIfPresent(distributorProductId);
        showToast({
          message: "Item added to cart successfully",
          variant: "success",
        });
      } catch (e) {
        console.error("Failed to add item to cart:", e);
        const serverMessage = (e as any)?.response?.body?.error;

        // Check if the error is about cart limit (look for the error message pattern)
        if (serverMessage && serverMessage.includes("You can only add up to")) {
          setOrderLimitMessage(serverMessage);
          setShowOrderLimitModal(true);
        } else {
          showToast({
            message:
              serverMessage || "Failed to add item to cart. Please try again",
            variant: "error",
          });
        }
      } finally {
        setIsAddingItemToCart(false);
      }
    },
    [
      isAddingItemToCart,
      removeSavedForLaterIfPresent,
      setCart,
      showToast,
      cartCount,
      refreshCartCount,
    ],
  );

  const columnKeysToShow = simplified
    ? SIMPLE_TABLE_ROW_KEY_FIELDS
    : ALL_TABLE_COLUMN_KEYS;
  const columns = useMemo(
    () =>
      filter(
        [
          {
            key: "vendor",
            header: "Vendor",
            render: (row: PricingRow) => (
              <div className="flex-row">
                <DistributorLogo
                  distributorSlug={row.distributorSlug}
                  className="margin-right-sm"
                />
                {row.isPreferred && (
                  <span className={`margin-left-sm ${styles.preferredStar}`}>
                    ★
                  </span>
                )}
              </div>
            ),
          },
          {
            key: "shipping",
            header: "Shipping",
            render: (row: PricingRow) => (
              <span className="text-sm">
                {row.isConnected ? row.shippingText : ""}
              </span>
            ),
          },
          {
            key: "availability",
            header: "Availability",
            render: (row: PricingRow) => (
              <span className="text-sm">
                {row.isConnected ? row.availabilityStatus : ""}
              </span>
            ),
          },
          // Note: Commented out for Beta due to missing functionality
          // {
          //   key: "rewards",
          //   header: "Rewards & Savings",
          //   render: (row: PricingRow) =>
          //     row.isConnected ? (
          //       <Badge
          //         ariaLabel="Greatest savings badge with money bill icon"
          //         icon={<i className="fa-light fa-money-bill-wave" />}
          //         label={row.rewardsLabel || "Greatest Savings"}
          //         variant="rewards"
          //       />
          //     ) : (
          //       <span />
          //     ),
          // },
          {
            key: "pricePerUnit",
            header: "Price per Unit",
            render: (row: PricingRow) => {
              if (!row.isConnected) {
                return <span className="text-sm" />;
              }
              return (
                <PriceDisplay
                  amount={row.pricePerUnit}
                  unavailableText="N/A"
                  className="text-sm"
                />
              );
            },
          },
          {
            key: "price",
            header: "Price",
            render: (row: PricingRow) => {
              const isExpanded = expandedPriceRows.has(row.id);
              const priceBreaks =
                row.originalData?.livePricing?.priceBreaks || [];
              const expansionIcon = isExpanded ? "▲" : "▼";

              // Only show API health messages for CONNECTED
              // NOT_CONNECTED distributors are filtered out entirely
              const connection = getDistributorConnection(row.distributorSlug);
              const isConnected =
                connection?.status === DistributorConnectionStatuses.Connected;

              if (isConnected) {
                if (
                  row.distributorApiHealthStatus ===
                  DistributorApiHealthStatuses.ScheduledMaintenance
                ) {
                  return (
                    <div className={styles.connectDistributorMessage}>
                      <span className="text-italic">
                        {distributorSlugToName(row.distributorSlug)} pricing is
                        currently unavailable due to scheduled maintenance
                      </span>
                    </div>
                  );
                }

                if (
                  row.distributorApiHealthStatus ===
                  DistributorApiHealthStatuses.ServiceOutage
                ) {
                  return (
                    <div className={styles.connectDistributorMessage}>
                      <span className="text-italic">
                        {distributorSlugToName(row.distributorSlug)} pricing is
                        currently unavailable due to a service outage
                      </span>
                    </div>
                  );
                }
              }

              if (!row.price) {
                return (
                  <span className="text-xxs text-secondary text-italic text-right">
                    Pricing Not Currently Available
                  </span>
                );
              }

              return (
                <div className={styles.priceColumn}>
                  <div>
                    <strong>
                      <PriceDisplay amount={row.price} />
                    </strong>
                    {/* place holder for min quantity until we get proper UI design for this edge case */}
                    {row.minQuantity > 1 && (
                      <span className="text-xs text-secondary font-weight-normal margin-left-xs">
                        ({row.minQuantity}+ qty)
                      </span>
                    )}
                  </div>
                  {row.hasMorePrices && (
                    <button
                      type="button"
                      className={`text-xs ${styles.morePricesLink}`}
                      onClick={() => togglePriceExpansion(row.id)}
                      aria-expanded={isExpanded}
                      style={{
                        cursor: "pointer",
                        background: "none",
                        border: "none",
                        padding: 0,
                      }}
                    >
                      +{row.priceBreaksCount} more prices {expansionIcon}
                    </button>
                  )}
                  {isExpanded && priceBreaks.length > 0 && (
                    <div className="margin-top-xs">
                      {priceBreaks.map((priceBreak, index) => (
                        <div
                          key={index}
                          className="text-xs"
                          style={{ color: "#666" }}
                        >
                          {priceBreak.quantity}+ qty:{" "}
                          <PriceDisplay amount={priceBreak.price} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            },
          },
          {
            key: "action",
            header: "",
            render: (row: PricingRow) => {
              const isScheduledProduct = !!row.originalData?.schedule;

              return (
                <RequiresRoleContainer role={Roles.ClinicUser}>
                  <div className="width-100 flex-row flex-end flex-center">
                    {/* {row.buttonDisabled && isScheduledProduct ? (
                      <span
                        className="text-secondary text-italic text-right text-xxs"
                        data-testid={`scheduled-product-contact-distributor-${row.distributorSlug}`}
                      >
                        {row.buttonText}
                      </span>
                    ) : ( */}
                    <>
                      <Button
                        className="btn btn-primary btn-sm"
                        size="medium"
                        variant="primary"
                        disabled={row.buttonDisabled || isAddingItemToCart}
                        onClick={() => {
                          if (
                            !isAddingItemToCart &&
                            !row.buttonDisabled &&
                            row.buttonText === "Add to Cart"
                          ) {
                            handleAddToCart(row.originalData.id);
                          }
                        }}
                        data-testid={`add-to-cart-button-${row.distributorSlug}`}
                      >
                        {row.buttonText}
                      </Button>
                      {isAddingItemToCart && (
                        <div
                          className="margin-left-sm flex-row flex-center"
                          style={{
                            position: "relative",
                            width: "16px",
                            height: "16px",
                            display: "inline-flex",
                          }}
                        >
                          <SpinningLoader size={SpinningLoaderSizes.Small} />
                        </div>
                      )}
                    </>
                    {/* )} */}
                  </div>
                </RequiresRoleContainer>
              );
            },
          },
        ],
        ({ key }) => columnKeysToShow.includes(key),
      ),
    [
      expandedPriceRows,
      handleAddToCart,
      isAddingItemToCart,
      togglePriceExpansion,
      getDistributorConnection,
    ],
  );

  return (
    <>
      <SimpleTable
        tableHeading={tableHeading}
        columns={columns as any}
        data={data}
        data-testid={dataTestId}
      />

      <Modal
        isShowing={showOrderLimitModal}
        onClose={() => setShowOrderLimitModal(false)}
        header="Order Limit Reached"
        size={ModalSizes.ExtraSmall}
        hideCloseButton={true}
        noDefaultPadding={true}
      >
        <div className={styles.orderLimitModalContent}>
          <p>
            {orderLimitMessage ||
              "You can only add up to 100,000 of each item to your cart."}
          </p>
          <button
            onClick={() => setShowOrderLimitModal(false)}
            className={`btn btn-primary btn-md text-center ${styles.orderLimitModalButton}`}
          >
            OK
          </button>
        </div>
      </Modal>
    </>
  );
}
